import React, { useState, useEffect } from "react";
import {
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Table,
  Modal,
  message,
  Spin,
  Tag,
} from "antd";
import { PlusOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  FetchGRNList_api,
  FetchGRNItems_api,
  AddGRN_api,
} from "../../Api/GRN/grn_api";
import {
  FetchProducts_api,
  AddProduct_api,
} from "../../Api/Products/product_api";
import { useAuthChecker } from "../../Context";
import dayjs from "dayjs";
import { Label } from "../../components/ui/label";

const { Option } = Select;

interface TempGRNItem {
  key: string;
  product_id: number;
  product_name: string;
  qty: number;
  cost: number;
  subtotal: number;
}

export const ManageGrnPage = () => {
  const { user } = useAuthChecker();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");

  // Add GRN Drawer
  const [grnDrawerOpen, setGrnDrawerOpen] = useState(false);
  const [grnForm] = Form.useForm();
  const [tempItems, setTempItems] = useState<TempGRNItem[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const [itemQty, setItemQty] = useState(1);
  const [itemCost, setItemCost] = useState(0);

  // View GRN Items Modal
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingGrnId, setViewingGrnId] = useState<number | null>(null);

  const [isGrnSaveLoading, setIsGrnSaveLoading] = useState(false);

  // Fetch GRN List
  const {
    data: grnData,
    refetch: refetchGRN,
    isFetching: isFetchingGRN,
  } = useQuery({
    queryKey: ["grn_list", page, pageSize, searchText],
    queryFn: () => FetchGRNList_api(page, pageSize, searchText),
  });

  // Fetch Products
  const { data: productsData } = useQuery({
    queryKey: ["products_all"],
    queryFn: () => FetchProducts_api(1, 1000, ""),
  });

  // Fetch GRN Items for viewing
  const { data: grnItemsData, isFetching: isFetchingItems } = useQuery({
    queryKey: ["grn_items", viewingGrnId],
    queryFn: () => FetchGRNItems_api(viewingGrnId!),
    enabled: !!viewingGrnId,
  });

  const handleViewGRN = (grnId: number) => {
    setViewingGrnId(grnId);
    setViewModalOpen(true);
  };

  const handleAddItemToTemp = () => {
    if (!selectedProduct || itemQty <= 0 || itemCost <= 0) {
      message.warning("Please select product and enter valid qty & cost");
      return;
    }

    const product = productsData?.products?.find(
      (p: any) => p.id === selectedProduct,
    );

    if (!product) return;

    const subtotal = itemQty * itemCost;
    const newItem: TempGRNItem = {
      key: Date.now().toString(),
      product_id: selectedProduct,
      product_name: product.name,
      qty: itemQty,
      cost: itemCost,
      subtotal,
    };

    setTempItems([...tempItems, newItem]);
    setSelectedProduct(null);
    setItemQty(1);
    setItemCost(0);
  };

  const handleRemoveTempItem = (key: string) => {
    setTempItems(tempItems.filter((item) => item.key !== key));
  };

  const handleSaveGRN = async () => {
    if (tempItems.length === 0) {
      message.warning("Please add at least one item");
      return;
    }

    setIsGrnSaveLoading(true);

    try {
      const values = await grnForm.validateFields();
      const payload = {
        invoice_id: values.invoice_id,
        invoice_date: values.invoice_date.format("YYYY-MM-DD"),
        items: tempItems.map((item) => ({
          product_id: item.product_id,
          qty: item.qty,
          cost: item.cost,
        })),
        userId: user?.userId,
      };

      const res = await AddGRN_api(payload);
      if (res.isDataUpdated) {
        message.success(res.msg);
        setGrnDrawerOpen(false);
        grnForm.resetFields();
        setTempItems([]);
        refetchGRN();
      } else {
        message.error(res.msg);
      }
    } catch (error) {
      console.error("GRN save error:", error);
    } finally {
      setIsGrnSaveLoading(false);
    }
  };

  const calculateTotal = () => {
    return tempItems.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const grnColumns = [
    {
      title: " ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Invoice ID",
      dataIndex: "invoice_id",
      key: "invoice_id",
    },
    {
      title: "Invoice Date",
      dataIndex: "invoice_date",
      key: "invoice_date",
      render: (date: string) => dayjs(date).format("MMM D, YYYY"),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      render: (total: any) => `Rs. ${parseFloat(total).toLocaleString()}`,
    },
    {
      title: "Added Date",
      dataIndex: "added_date",
      key: "added_date",
      render: (date: string) => dayjs(date).format("MMM D, YYYY HH:mm"),
    },
    {
      title: "Action",
      key: "action",
      align: "center" as const,
      render: (_: any, record: any) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewGRN(record.id)}
        >
          View Items
        </Button>
      ),
    },
  ];

  const tempItemColumns = [
    {
      title: "Product",
      dataIndex: "product_name",
      key: "product_name",
    },
    {
      title: "Qty",
      dataIndex: "qty",
      key: "qty",
    },
    {
      title: "Cost",
      dataIndex: "cost",
      key: "cost",
      render: (cost: number) => `Rs. ${cost.toLocaleString()}`,
    },
    {
      title: "Subtotal",
      dataIndex: "subtotal",
      key: "subtotal",
      render: (subtotal: number) => `Rs. ${subtotal.toLocaleString()}`,
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: TempGRNItem) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveTempItem(record.key)}
        />
      ),
    },
  ];

  const viewItemColumns = [
    {
      title: "Product",
      dataIndex: "product_name",
      key: "product_name",
    },
    {
      title: "Qty",
      dataIndex: "qty",
      key: "qty",
    },
    {
      title: "Cost",
      dataIndex: "cost",
      key: "cost",
      render: (cost: string) => `Rs. ${parseFloat(cost).toLocaleString()}`,
    },
  ];

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">GRN Management</h1>
        <div className="flex gap-2">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setGrnDrawerOpen(true)}
          >
            Add GRN
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <Table
          columns={grnColumns}
          dataSource={grnData?.grnList || []}
          rowKey="id"
          loading={isFetchingGRN}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: grnData?.total || 0,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} GRNs`,
          }}
          className="overflow-x-auto"
        />
      </div>

      {/* Add GRN Drawer */}
      <Drawer
        title="Add GRN"
        open={grnDrawerOpen}
        onClose={() => {
          setGrnDrawerOpen(false);
          grnForm.resetFields();
          setTempItems([]);
        }}
        width="70%"
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setGrnDrawerOpen(false)}>Cancel</Button>
            <Button loading={isGrnSaveLoading} type="primary" onClick={handleSaveGRN}>
              Save GRN
            </Button>
          </div>
        }
      >
        <Form form={grnForm} layout="vertical">
          <div className="grid grid-cols-2 gap-4 mb-2">
            <Form.Item
              label="Invoice Number"
              name="invoice_id"
              rules={[{ required: true, message: "Required" }]}
            >
              <Input placeholder="Invoice number" />
            </Form.Item>
            <Form.Item
              label="Invoice Date"
              name="invoice_date"
              rules={[{ required: true, message: "Required" }]}
            >
              <DatePicker className="w-full" />
            </Form.Item>
          </div>
        </Form>

        <div className="border rounded-lg p-4 bg-gray-50">
          <h3 className="text-sm font-semibold mb-4">Add Item</h3>
          <div className="grid grid-cols-4 gap-3 items-end ">
            <div className="flex flex-col">
              <Label className="text-sm font-semibold">Product</Label>
              <Select
                placeholder="Select Product"
                value={selectedProduct}
                onChange={setSelectedProduct}
                showSearch
                optionFilterProp="children"
              >
                {productsData?.products?.map((item: any) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold" htmlFor="cost">
                Cost
              </Label>
              <InputNumber
                name="cost"
                placeholder="Cost"
                min={0}
                value={itemCost}
                onChange={(val) => setItemCost(val || 0)}
                className="w-full"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold" htmlFor="qty">
                Qty
              </Label>
              <InputNumber
                name="qty"
                placeholder="Qty"
                min={1}
                value={itemQty}
                onChange={(val) => setItemQty(val || 1)}
                className="w-full"
              />
            </div>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddItemToTemp}
            >
              Add Item
            </Button>
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden mb-4">
          <div className="p-3 bg-gray-100 font-semibold flex justify-between items-center">
            <span>Items</span>
            <Tag color="blue">
              Total: Rs. {calculateTotal().toLocaleString()}
            </Tag>
          </div>
          <Table
            columns={tempItemColumns}
            dataSource={tempItems}
            rowKey="key"
            pagination={false}
            locale={{ emptyText: "No items added" }}
            size="small"
            className="overflow-x-auto"
          />
        </div>
      </Drawer>

      {/* View GRN Items Modal */}
      <Modal
        title="GRN Items"
        open={viewModalOpen}
        onCancel={() => {
          setViewModalOpen(false);
          setViewingGrnId(null);
        }}
        footer={null}
        width={600}
      >
        {isFetchingItems ? (
          <div className="flex justify-center p-8">
            <Spin />
          </div>
        ) : (
          <Table
            columns={viewItemColumns}
            dataSource={grnItemsData?.items || []}
            rowKey="id"
            pagination={false}
            size="small"
            className="overflow-x-auto"
          />
        )}
      </Modal>
    </div>
  );
};
